import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { T, t } from '@sm/intl';
import { Modal } from '@wds/modal';
import { Button } from '@wds/button';
import { Link } from '@wds/link';
import { Textarea } from '@wds/textarea';

import CreateSeatRequestMutation from './graphql/CreateSeatRequest.graphql';
import COPY from './SeatRequestCopy';
import { STATUS } from './constants';
import { StaticContext } from '../StaticContext';
import { getHelpCenterLink } from '../../helpers/getHelpLink';

const MAX_MESSAGE_LENGTH = 400;

const useArticleHelpLink = path => {
  const staticContext = useContext(StaticContext);

  return getHelpCenterLink(staticContext?.user.language, {
    path,
  });
};

const LinkRenderer =
  (link, newTab = false) =>
  ({ children }) =>
    (
      <Link
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noreferrer noopener' : undefined}
        href={link}
      >
        {children}
      </Link>
    );

LinkRenderer.propTypes = {
  newTab: PropTypes.bool.isRequired,
  helpLink: PropTypes.string.isRequired,
};

const RequestModalContent = ({
  onModalFinished,
  sourceCategoryKey,
  sourceFeatureKey,
  isCasualUserAllowExport,
}) => {
  const [reason, setReason] = useState('');
  const [createSeatRequest, { loading }] = useMutation(
    CreateSeatRequestMutation,
    {
      onError: () => {
        onModalFinished({
          status: STATUS.FAILURE,
          message: t(COPY.TOAST_ERROR),
        });
      },
      onCompleted: () => {
        setReason('');
        onModalFinished({
          status: STATUS.SUCCESS,
          message: t(COPY.TOAST_REQUEST_SENT),
        });
      },
    }
  );

  const onSubmit = () => {
    createSeatRequest({
      variables: {
        input: {
          seatType: 'POWER_USER',
          reason: reason.length > 0 ? reason : null,
          sourceCategoryKey,
          sourceFeatureKey,
        },
      },
    });
  };
  const helpLink = useArticleHelpLink('teams/enterprise-seats');

  return (
    <>
      <Modal.Header header={<T desc={COPY.MODAL_TITLE_AVAILABLE} />} />
      <Modal.Body>
        <p>
          <b>
            <T desc={COPY.REQUEST_DESC_1} />
          </b>
        </p>
        <p>
          <T
            desc={
              isCasualUserAllowExport
                ? COPY.REQUEST_DESC_2_ALLOW_EXPORT
                : COPY.REQUEST_DESC_2
            }
            values={{
              'help-link': LinkRenderer(helpLink, true),
            }}
          />
        </p>
        <Textarea
          label={t(COPY.REASON_LABEL)}
          value={reason}
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={e => setReason(e.target.value)}
          stretched
        />
      </Modal.Body>
      <Modal.Footer align="right">
        <Button
          variant="ghost"
          color="secondary"
          disabled={loading}
          onClick={onModalFinished}
        >
          <T desc={COPY.CANCEL_BUTTON} />
        </Button>
        <Button onClick={onSubmit} disabled={loading}>
          <T desc={COPY.SEND_BUTTON} />
        </Button>
      </Modal.Footer>
    </>
  );
};

RequestModalContent.propTypes = {
  onModalFinished: PropTypes.func.isRequired,
  sourceCategoryKey: PropTypes.string.isRequired,
  sourceFeatureKey: PropTypes.string,
  isCasualUserAllowExport: PropTypes.bool,
};

RequestModalContent.defaultProps = {
  sourceFeatureKey: null,
  isCasualUserAllowExport: false,
};

export default RequestModalContent;
